<template lang="pug">
div.home-front-pages(
    v-observe-visibility="observeVisibilityOptions"
    v-touch:swipe.left="debouncedNext"
    v-touch:swipe.right="debouncedPrevious"
    v-touch-options="{swipeTolerance: 30}"
    v-bind:style="backgroundStyle")

    //- taide.art intro logo page
    home-hero-page.taide-art(
        v-if="(currentHero && currentHero.Name === 'intro' && isIntersectionVisible) || heros.length <= 1"
        ref="heroPage"
        v-bind:spotlight="introHero"
        v-bind:next-item="nextItem"
        v-bind:class="(showInfobox ? 'infobox-visible' : 'infobox-hidden') + (coverImageIndex < 1 ? ' initial' : '')")

        template(#infobox="")

            div.logo
                image-box(v-bind:src="require('assets/images/taide-art-white.svg')" image-alt-text="taide.art")
                    template(#not-available="")
                        div.is-hidden

            //- is user waiting for login confirmation?
            div.logging-in(v-if="loginPhase === phaseBusy")
                loading-spinner

            div.call-to-action(v-else)
                h3(v-html="$t('home.callToAction')")


    //- PageItem "hero-page"
    //- This sets the big background image! Only one is rendered in html at one time!
    home-hero-page(
        v-else-if="isIntersectionVisible"
        ref="heroPage"
        v-bind:spotlight="currentItem"
        v-bind:resource-type="currentResourceType"
        v-bind:next-item="nextItem"
        v-bind:class="showInfobox ? 'infobox-visible' : 'infobox-hidden'")

        template(#infobox="")

            artwork-card(
                v-if="currentResourceType === 'Artwork'"
                v-bind:item="currentItem"
                v-bind:link-to-artwork="true"
                v-bind:social="false"
                v-bind:media="false")

            feature-article-card(
                v-else-if="currentResourceType === 'FeatureArticle'"
                v-bind:item="currentItem"
                v-bind:hero="true"
                v-bind:header="false"
                v-bind:class="coverImageIndex % 2 === 0 ? 'odd' : 'even'")

            collection-card(
                v-else-if="currentResourceType === 'Collection'"
                v-bind:item="currentItem"
                v-bind:social="false")
                div.tagline(
                    v-if="currentItem.TagLine") {{ currentItem.TagLine }}
                div.ingress-text(
                    v-if="currentItem.IngressText" v-html="currentItem.IngressText")
                div.body-html(
                    v-if="currentItem.BodyHtml" v-html="currentItem.BodyHtml")

            event-card(
                v-else-if="currentResourceType === 'Event'"
                v-bind:item="currentItem"
                v-bind:resolution="{x: '100vw', y: '100vh'}"
                v-bind:social="false")
                div.tagline(
                    v-if="currentItem.TagLine") {{ currentItem.TagLine }}
                div.ingress-text(
                    v-if="currentItem.IngressText" v-html="currentItem.IngressText")

            actor-highlights(
                ref="actorHighlights"
                v-else-if="currentResourceType === 'Actor'"
                v-bind:items="[currentItem]"
                v-bind:slick-options="unSlickOptions"
                v-bind:show-body="false"
                v-bind:show-artworks="false")


    //- screen overlay actions (prev and next icons) - disabled
    //- div.hero-navigation
    //-     icon-chevron-left.previous-icon(
    //-         v-on:click="debouncedPrevious"
    //-         title="")
    //-     icon-chevron-right.next-icon(
    //-         v-on:click="debouncedNext"
    //-         title="")


</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import HomeHeroPage from '@/components/Home/HeroPage.vue';
import HeroMixin from '@/components/Home/HeroMixin.js';
import IntersectionObserverMixin from '@/components/Base/IntersectionObserverMixin.js';
import { phaseBusy, phaseAuthenticated } from '@/store/ux.js';

const mapStateUx = createNamespacedHelpers('ux').mapState;

const PAGE_UPDATE_INTERVAL = 12000;
const INFOBOX_FADEIN_TIME = 1250;
const INFOBOX_FADEOUT_TIME = 1500;

export default {
    name: 'home-front-pages',

    props: {
        svcPages: {          type: Array[Object] }
    },

    mixins: [ HeroMixin, IntersectionObserverMixin ],

    components: {
        HomeHeroPage,
        ActorHighlights: () => import(/* webpackPrefetch: true */'@/components/Actor/Highlights')
    },

    computed: {
        ...mapStateUx({
            loginPhase: state => state.loginPhase,
            frontPageHeroIndex: state => state.frontPageHeroIndex,
            frontPageHeroIndexTime: state => state.frontPageHeroIndexTime,
            frontPageCoverIndex: state => state.frontPageCoverIndex
        })
    },

    data: function () {
        return {
            // heros will be appended with PageItems
            heros: [],
            // heroIndex is the main loop index, 0 being intro logo page
            heroIndex: 0,
            // intro hero
            introHero: {
                Name: 'intro',
                ClassName: 'hero-intro',
                CoverImageInfo: {
                    Url: require('static/static/coverimages/background-1.jpg')
                },
                ResourceType: 'intro',
                anchor: {
                    fi: 'etusivu',
                    en: 'intro',
                    sv: 'intro'
                }
            },
            // currentHero is intro page (object or null) or PageItem
            currentHero: null,
            // currentItem is the resource unwrapped from PageItem
            currentItem: null,
            currentResourceType: null,
            // next item to preload
            nextItem: null,
            // coverImages are for intro logo page background cover
            coverImages: [],
            coverImageIndex: 0,
            showInfobox: true,
            infoboxTimeoutRef: null,
            animationFrameRef: null,
            animationFrameInterval: 2000,
            animationFrameTimeoutRef: null,
            heroFrameReset: false,
            heroFrameTimestamp: 0,
            playing: false,
            backgroundStyle: '',
            phaseBusy,
            // init is called when authentication state and svc pages change
            debouncedInit: _.debounce(this.init, 200, {trailing: true}),
            // debouncing prevents user action flood, called by swipe gestures and chevron icons.
            // action starts at leading edge, but another one won't be accepted within the given timeframe
            debouncedNext: _.debounce(this.next, 1500, {leading: true}),
            debouncedPrevious: _.debounce(this.previous, 1500, {leading: true}),
            observeVisibilityOptions: {
                callback: this.visibilityChanged,
                throttle: 300,
                throttleOptions: {leading: 'visible'},
                intersection: {
                    rootMargin: '1200px 0px',
                    threshold: 0
                }
            },
            unSlickOptions: {
                slidesToShow: 1,
                rows: 1,
                arrows: false
            }
        };
    },

    created: function () {
        this.heros = this.getHeros();
        this.currentHero = this.introHero;

        this.coverImages = this.getPagesCoverImages();
        // show static cover image until pages have loaded
        if (!this.coverImages || !this.coverImages.length) {
            this.coverImages = [this.introHero.CoverImageInfo];
        }
        // restore stored cover index
        this.coverImageIndex = this.frontPageCoverIndex;

        // background css is heavy to load
        // this.backgroundStyle = this.setLoadingBackgroundStyle();

        // set the max-height of the element --
        // NOTE: there is an issue where coming back from auth0 login screen will cause the front-page image
        //       height to increase to 4096px distorting the image. In this case window.innerHeight will give
        //       a wrong value. Setting this container height so that the image would not grow too large.
        this.backgroundStyle = `max-height: ${window.screen.availHeight}px;`; // eslint-disable-line
    },

    mounted: function () {
        // global API method to change to given page and stop the loop
        window.TAIDEART.showFrontPage = this.changeToPage;
        window.TAIDEART.stopFrontPageLoop = this.stop;
    },

    beforeDestroy: function () {
        this.stop();
    },

    watch: {
        /**
         * When svc/pages (passed as props) are fetched and in store, the loop may begin.
         */
        svcPages: function () {
            this.heros = this.getHeros();
            this.coverImages = this.getPagesCoverImages();
            this.debouncedInit();
        },

        /**
         * Change in authentication state
         */
        loginPhase: function () {
            this.debouncedInit();
        },

        /**
         * Flag used to keep track of current page timeout reset
         */
        currentHero: function () {
            this.heroFrameReset = true;
        }
    },

    methods: {
        /**
         * Prepare and update initial page.
         * If the user is authenticated, start directly from PageItems.
         */
        init: function () {
            // frontPageHeroIndexTime will hard reset the page loop every 2 days
            const millisec_diff = new Date().getTime() - this.frontPageHeroIndexTime;
            const days = Math.floor(millisec_diff / 1000 / 60 / (60 * 24));

            if (days > 2) {
                // hero index time can be updated once all pages loop
                // (this is used to reset the cycle after a certain amount of dats)
                this.$store.commit('ux/setFrontPageHeroIndexTime', new Date().getTime());

                // increase counter (used for logo page CoverImage and minor visual variations on other pages)
                this.coverImageIndex += 1;
                this.$store.commit('ux/setFrontPageCoverIndex', this.coverImageIndex);

                this.reset();
            }
            else {
                this.heroIndex = this.frontPageHeroIndex; // stored value
            }

            // authenticated users start the hero index loop from the first PageItem
            if (this.loginPhase >= phaseAuthenticated && this.heroIndex === 0) {
                this.heroIndex = 1;
            }

            this.currentHero = this.heros[this.heroIndex];
            this.currentResourceType = this.getHeroResourceType(this.currentHero);
            this.currentItem = this.currentResourceType && this.currentHero[this.currentResourceType];

            // get next hero to preload the bg image
            this.nextItem = this.getNextItem();

            // with more heros than the static front page, start the loop
            if (this.heros.length > 1 && !this.playing) {
                this.play();
            }
        },

        /**
         * Heros combine main site intro landing page and svc/pages PageItems.
         */
        getHeros: function () {
            return _.compact([this.introHero].concat(this.svcPages.PageItems));
        },

        /**
         * Intro logo page background images.
         */
        getPagesCoverImages: function () {
            return this.svcPages.CoverImages;
        },

        /**
         * Update to the next hero, in a loop,
         * returning to the first "intro" hero with logo and dynamic background from CoverImages.
         */
        update: function () {
            if (!this.heros || !this.heros.length) {
                return;
            }

            // loop heros with modulo
            this.heroIndex = this.heroIndex % this.heros.length;

            // change current page contents
            this.currentHero = this.heros[this.heroIndex] || {};
            this.currentResourceType = this.getHeroResourceType(this.currentHero);

            if (this.heroIndex === 0) {
                // change intro logo page background
                const coverImageCount = this.coverImages && this.coverImages.length;
                if (coverImageCount) {
                    this.currentHero.CoverImageInfo = this.coverImages[this.coverImageIndex % coverImageCount];
                }
                this.currentItem = this.introHero;
            }
            else {
                // get item by its resource key (from PageItems)
                this.currentItem = this.currentHero[this.currentResourceType];
            }

            // get next hero to preload the bg image
            this.nextItem = this.getNextItem();

            // empty item that is not intro page, skip to next
            if (!this.currentItem && this.currentResourceType !== 'intro') {
                this.heroIndex += 1;
                this.update();
            }
        },

        /**
         * Get the (unwrapped) item from next hero.
         */
        getNextItem: function () {
            const nextItem = this.heros[((this.heroIndex + 1) % this.heros.length)] || null;
            if (nextItem) {
                // next intro cover image
                if (nextItem.Name === 'intro') {
                    const coverImageCount = this.coverImages && this.coverImages.length;
                    if (coverImageCount) {
                        return {CoverImageInfo: this.coverImages[this.coverImageIndex % coverImageCount]};
                    }
                    else {
                        return null;
                    }
                }

                // unwrap next hero item
                const type = this.getHeroResourceType(nextItem);
                return type && nextItem[type];
            }
        },

        /**
         * Page update interval, depends on network connection speed (`navigator.connection.downlink`)
         * Pages are given more time with slow connections on the first loop
         */
        getPageInterval: function () {
            let interval = PAGE_UPDATE_INTERVAL;

            // initial front page changes faster
            if (this.heroIndex === 0) {
                interval = interval / 3;
            }

            // increase the first hero loop for slow downlink speeds
            if ((this.frontPageCoverIndex < 1) &&
                (navigator && navigator.connection && navigator.connection.downlink)) {

                // increase interval on slow connections
                if (navigator.connection.downlink < 0.5) {
                    interval *= 1.25;
                }
                else if (navigator.connection.downlink < 1.5) {
                    interval *= 1.15;
                }
                else if (navigator.connection.downlink < 2.5) {
                    interval *= 1.05;
                }
            }
            return interval;
        },

        /**
         * Get Resource key from PageItem.
         */
        getHeroResourceType: function (hero) {
            let key;
            if (this.heroIndex > 0 && hero) {
                key = Object.keys(hero)[0];
            }
            return key || (hero && hero.ResourceType);
        },

        showInfoboxWithTimeout: function (fadeinTime) {
            if (this.infoboxTimeoutRef) {
                clearTimeout(this.infoboxTimeoutRef);
            }
            this.infoboxTimeoutRef = setTimeout(this.showInfoboxNow, fadeinTime || INFOBOX_FADEIN_TIME);
        },

        showInfoboxNow: function () {
            if (this.infoboxTimeoutRef) {
                clearTimeout(this.infoboxTimeoutRef);
            }
            this.showInfobox = true;

            // reslick actors
            this.$refs.actorHighlights && this.$refs.actorHighlights.$refs.SlickSlides &&
                this.$refs.actorHighlights.$refs.SlickSlides.reSlick();
        },

        hideInfoboxNow: function () {
            if (this.infoboxTimeoutRef) {
                clearTimeout(this.infoboxTimeoutRef);
            }
            this.showInfobox = false;

            return new Promise((resolve) => {
                // wait for transform-duration to resolve Promise
                this.infoboxTimeoutRef = setTimeout(resolve, 510);
            });
        },

        /**
         * Callback for `requestAnimationFrame`
         */
        animate: function (timestamp) {
            // dynamic interval between hero page change (ms)
            const heroFrameT1 = this.getPageInterval();

            if (this.heroFrameReset) {
                this.heroFrameTimestamp = timestamp;
                this.heroFrameReset = false;
            }
            // time delta in this frame
            const heroFrameTDelta = timestamp - this.heroFrameTimestamp;

            // update page if time delta exceeds interval
            if (this.playing) {
                if (heroFrameTDelta > heroFrameT1) {
                    // if playing the loop, update page
                    if (this.playing) {
                        this.debouncedNext();
                        // set timestamp to when page will change
                        this.heroFrameTimestamp = timestamp + 500; // + transition duration
                    }
                    // skip next frame request, if loop is stopped then animation will also be stopped
                    return;
                }
                // page background still visible, hide infobox a few seconds before change
                else if (this.showInfobox && heroFrameTDelta > (heroFrameT1 - INFOBOX_FADEOUT_TIME)) {
                    this.hideInfoboxNow();
                }
                // infobox is hidden during page change, and appears soon after
                else if (!this.showInfobox && heroFrameTDelta > INFOBOX_FADEIN_TIME) {
                    this.showInfoboxNow();
                }
            }
            else if (!this.showInfobox) {
                this.showInfoboxNow();
            }

            // delay next hero frame
            this.animationFrameTimeoutRef = setTimeout(() => {
                this.animationFrameRef = requestAnimationFrame(this.animate);
            }, this.animationFrameInterval);
        },

        /**
         * Loop with recursive `requestAnimationFrame`
         */
        play: function () {
            this.playing = true;
            this.animationFrameRef = requestAnimationFrame(this.animate);
        },

        stop: function () {
            this.playing = false;
            // prevent next animation frame
            if (this.animationFrameRef) {
                cancelAnimationFrame(this.animationFrameRef);
                this.animationFrameRef = null;
            }
            // prevent next frame request
            if (this.animationFrameTimeoutRef) {
                clearTimeout(this.animationFrameTimeoutRef);
                this.animationFrameTimeoutRef = null;
            }
            if (this.infoboxTimeoutRef) {
                clearTimeout(this.infoboxTimeoutRef);
                this.infoboxTimeoutRef = null;
            }
        },

        next: function () {
            // can exceed `heros` length
            this.changeToPage(this.heroIndex + 1);
        },

        previous: function () {
            // a negative index will not go to the last page from previous() of the first page
            let heroIndex = this.heroIndex - 1;
            if (heroIndex < 0) {
                heroIndex += this.heros.length;
            }
            this.changeToPage(heroIndex);
        },

        reset: function () {
            this.stop();
            this.heroIndex = 0;
            this.currentHero = this.heros[this.heroIndex];
            this.showInfoboxNow();
            // this.coverImageIndex = 0; // coverImageIndex intentionally not reset
        },

        /**
         * Change to given page in `heros`
         */
        changeToPage: function (heroIndex) {
            // stop animation, prevent next update
            this.stop();

            // update store value (computed property has no setter)
            this.$store.commit('ux/setFrontPageHeroIndex', heroIndex);
            this.heroIndex = heroIndex;

            this.hideInfoboxNow()
                .then(() => {
                    // user may have navigated out of the page, observer stopped the loop, but this occurs
                    this.update();
                    if (!this.playing) {
                        this.play();
                    }
                });
        },

        /**
         * Get loading background gradient from palette colors. Defaults to scss gradient.
         */
        // setLoadingBackgroundStyle: function (imageInfo) {
        //     if (!imageInfo) {
        //         this.loadingBackgroundStyle = ''; // css default
        //         return;
        //     }
        //     this.loadingBackgroundStyle = this.$helpers.getPaletteColorsBackgroundStyle(imageInfo);
        // },

        /**
         * Reset to intro when hidden (scrolled down)
         */
        onIntersectHide: function () {
            this.stop();
        },

        /**
         * Loop starts when intersect is shown, if ../pages are ready.
         * The loop might be started in the svcPages watcher, as well.
         */
        onIntersectShow: function () {
            this.debouncedInit();
        }
    }
};
</script>

<style lang="scss">
// @import '~assets/styles/mixins';

.home-front-pages {
    position: relative;
    width: 100vw;
    height: 100vh;

    // common background gradient to show until the image loads
    background-image:
        linear-gradient(
            180deg,
            rgba(2, 0, 36, 1) 0%,
            rgba(23, 30, 47, 1) 33%,
            rgba(45, 63, 105, 1) 57%,
            rgba(171, 160, 146, 1) 100%
        );

    // taide.art front page
    .taide-art {
        .infobox {
            position: relative;
            top: 0 !important;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            // justify-content: flex-end; // with flex-direction: column-reverse
            height: auto;
            user-select: none;
            transform: translateX(0) translateY(0) !important;
            transition-property: transform;
            transition-duration: 500ms;
            transition-timing-function: ease-in-out;
            h4 {
                font-family: "Sabon LT W01 Bold", serif;
                font-size: 2em;
            }
            em {
                font-family: "Sabon LT W01 Bold", serif;
                font-size: 1.5em;
            }
            h2 { // join
                margin: 0.75rem auto 1.5rem;
                font-family: "Sabon LT W01 Regular", serif;
                font-size: 32px;
                letter-spacing: -1px;
                text-transform: uppercase;
            }
            .call-to-action {
                display: inline-block;
                padding: 1.25em 1.5em;
                background-color: rgba(5, 5, 0, 0.50);
                box-shadow: 2px 2px 8px rgba(5, 5, 0, 0.95);
            }
            .call-to-action h3 {
                width: 100%;
                margin: auto;
                padding: 0;
                font-size: 1.5rem;
                color: white;
                text-align: center;
            }
            .logo {
                width: auto;
                height: auto;
                margin-top: 7vh;
                margin-bottom: 4rem;
                background:
                    radial-gradient(
                        farthest-side at 50% 51%,
                        rgba(5, 5, 0, 0.70),
                        rgba(5, 5, 0, 0.40) 30%,
                        rgba(25, 25, 25, 0.50) 35%,
                        rgba(5, 5, 0, 0.40) 40%,
                        rgba(5, 5, 0, 0.40) 50%,
                        rgba(25, 25, 25, 0.50) 55%,
                        rgba(5, 5, 0, 0.40) 60%,
                        rgba(5, 5, 0, 0.20) 80%,
                        rgba(5, 5, 0, 0) 97%
                    );
                figure .image {
                    height: 220px !important; // taide.art svg logo
                    margin-right: auto;
                    margin-left: auto;
                }
            }
            .buttons {
                align-content: center;
                justify-content: center;
                margin-top: 1rem;
                button.btn {
                    align-self: center;
                    &.login {
                        padding: 8px 0.8rem !important;
                        font-family: $helvetica-medium !important;
                        color: white !important;
                        background: transparent !important;
                        border: 2px solid white !important;
                    }
                }
            }
            @media screen and (min-width: $breakpoint-widescreen) {
                width: 100%;
            }
            @media screen and (max-width: $breakpoint-tablet - 1) {
                width: 100% !important;
                min-width: unset;
                max-width: 90vw;
                margin: auto;
                margin-top: 7vh;
                padding-right: 0;
                padding-left: 0;
                .logo {
                    width: 100%;
                }
                .call-to-action {
                    max-width: 375px;
                    margin: auto;
                    font-size: smaller;
                }
            }
        }
        @media screen and (max-width: $breakpoint-mobile - 1) {
            padding-top: 11vh;
            .infobox {
                .logo {
                    margin-top: 4vh;
                    figure .image {
                        width: 50vw !important;
                        height: auto !important;
                    }
                }
                .call-to-action {
                    max-width: unset;
                }
                .buttons {
                    flex-direction: column-reverse;
                }
            }
            .card {
                max-width: unset;
            }
        }
        @media screen and (min-width: $breakpoint-desktop) {
            padding-top: 15vh;
        }
    }

    // initial infobox style
    .initial.taide-art .infobox {
        .call-to-action {
            background-color: unset;
            box-shadow: none;
        }
        .logo {
            background: none;
        }
    }

    .hero-page {

        &.infobox-visible .infobox {
            opacity: 1;
        }
        &.infobox-hidden .infobox {
            opacity: 0;
        }

        .card {
            width: auto !important;
            max-width: 80vw;
            padding: 1.25rem;
            color: $dark-gray !important;
            user-select: none;
            background: rgba(255, 251, 251, 0.5) !important;
            backdrop-filter: blur(5px);
            .card-link {
                text-decoration: none;
                .title {
                    text-decoration: none;
                }
            }
            .tagline {
                margin: 0 auto 1rem 2rem;
                font-family: 'Sabon MT W01 Italic', serif;
                font-weight: 600;
                font-size: 1rem;
            }
            .ingress-text {
                margin-bottom: 1rem;
            }
            .card-header-title {
                cursor: pointer;
                @include dowrap;
                h2 {
                    @include dowrap;
                }
            }
            .body-html {
                @include fade-read-more(350px);
                &::after {
                    background-image: none;
                }
            }
        }

        .artwork-card {
            padding-bottom: 0.5rem;
            background:
                linear-gradient(
                    to top,
                    rgba(255, 244, 241, 0.70),
                    rgba(255, 255, 255, 0.80) 5%,
                    rgba(255, 251, 251, 0.90) 95%,
                    rgba(255, 244, 241, 0.80)
                ) !important;
            backdrop-filter: blur(5px);
            border: 0;
            // box-shadow: -2px 1px 14px rgba(255, 251, 251, 0.45);
            .actor-card {
                margin-top: 1.25rem;
                padding: 0;
                background: unset;
                backdrop-filter: none;
            }
            .card-header {
                background: unset;
                a.card-link {
                    background: unset;
                }
                // hide certain artwork details
                .artworks-counted,
                .lifetime {
                    display: none;
                }
                .title {
                    text-decoration: none;
                }
                .card-header-title {
                    color: $dark-gray-2 !important;
                }
            }
            .card-contents {
                margin: 1.5rem 0 1rem !important;
                .owner {
                    color: $dark-gray-4 !important;
                }
            }
            &.card .card-header-title .makers {
                font-size: 1.5rem;
                color: $dark-gray-2 !important;
            }
            &.card .card-header-title .title {
                font-size: 1.5rem;
                color: $dark-gray-2 !important;
            }
            .details {
                display: none;
            }
            .details.production-timespan,
            .details.keeper {
                display: block;
            }
        }

        .collection-card {
            color: $light-gray !important;
            background: rgba(5, 5, 0, 0.75) !important;
            .card-header {
                margin-bottom: 1rem;
                background: transparent !important;
                .card-header-title {
                    color: $medium-gray-3 !important;
                }
                // stylelint-disable-next-line no-descending-specificity
                .title {
                    font-family: $main-sans-serif;
                    font-size: 1.5rem;
                    letter-spacing: 1px;
                    color: $medium-gray-3 !important;
                    text-shadow: 1px 1px 1px $dark-gray-2;
                }
                .curator {
                    margin-top: 1rem;
                    font-size: 1.25rem;
                }
            }
        }

        .feature-article-card {
            padding: 1.5rem 0 2.5rem;
            background:
                linear-gradient(
                    to top,
                    rgba(255, 244, 241, 0.05),
                    rgba(255, 255, 255, 0.50) 10%,
                    rgba(255, 251, 251, 0.85) 95%,
                    rgba(255, 244, 241, 0.64)
                ) !important;
            backdrop-filter: blur(5px);
            border-color: rgba(124, 124, 124, 0.04);
            &.even {
                border-color: rgba(251, 204, 241, 0.04);
            }
            border-style: groove;
            border-width: 0 1rem;
            .card-header {
                background: unset;
                // stylelint-disable-next-line no-descending-specificity
                .title {
                    font-size: 2rem;
                }
            }
            .card-contents {
                width: 100%;
                margin-left: 0 !important;
                color: $dark-gray-2 !important;
                background: unset !important;
            }
            .article-ingress {
                max-width: unset;
                h2 {
                    font-size: 1.75rem;
                    color: $dark-gray-2 !important;
                }
            }
            .publish-date {
                display: none !important;
            }
        }

        .event-card {
            color: $light-gray !important;
            background:
                linear-gradient(
                    to right top,
                    rgba(5, 5, 0, 0.95),
                    rgba(5, 5, 0, 0.90) 30%,
                    rgba(9, 9, 5, 0.85) 77%,
                    rgba(5, 5, 0, 0.94)
                ) !important;
            box-shadow: 2px 2px 8px rgba(5, 5, 0, 0.95);
            .card-header {
                margin-bottom: 1rem;
                background: transparent !important;
                h2 {
                    color: $light-gray !important;
                }
                // stylelint-disable-next-line no-descending-specificity
                .title {
                    font-family: $main-sans-serif;
                    font-size: 1.5rem;
                    line-height: 2.75rem;
                    letter-spacing: 1px;
                    text-shadow: 1px 1px 1px $medium-gray;
                }
                .organizer-name {
                    color: $light-gray !important;
                }
                .event-period {
                    margin-top: 0.5rem;
                    color: $light-gray !important;
                }
                .card-link:hover {
                    .event-period {
                        text-decoration: none;
                    }
                }
            }
            .ingress-text {
                font-size: 1.2rem;
            }
        }

        &.hero-Actor {
            .slick-slider {
                width: 100%;
                max-width: 98vw;
            }
            .actor-slides {
                width: 100%;
                min-height: 300px;
            }
            .infobox {
                width: 100%;
                max-width: 98vw;
                transform: unset !important;
                .columns {
                    display: inline-flex;
                    flex-direction: column;
                }
                .column {
                    width: auto;
                }
                .actor-card {
                    display: inline-flex !important;
                    flex-direction: column;
                    width: clamp(300px, 400px, 95vw) !important;
                    margin-right: auto;
                    background: unset !important;
                    transform: unset !important;
                    &.is-right {
                        margin-left: auto;
                    }
                    &.is-left {
                        margin-right: auto;
                    }
                }
                .actor-card,
                .card-header {
                    backdrop-filter: unset;
                }
                .card-header {
                    align-self: center;
                    margin: 0.5rem auto;
                    padding: 3rem 4rem !important;
                    background:
                        radial-gradient(
                            farthest-side at 50% 50%,
                            rgba(255, 251, 251, 0.85) 15%,
                            rgba(255, 251, 251, 0.5) 65%,
                            rgba(255, 251, 251, 0) 100%
                        ) !important;
                    .title {
                        color: $dark-gray-2 !important;
                        text-shadow: 1px 1px 1px $medium-gray-2 !important;
                    }
                    @media screen and (max-width: $breakpoint-mobile - 1) {
                        padding: 1rem;
                        figure {
                            margin: auto;
                        }
                    }
                }
                .card-social {
                    display: none;
                }
                .card-image {
                    display: inline-flex;
                    flex-grow: 2;
                    align-self: center;
                    margin: 0 auto !important;
                }
                .actor-body {
                    width: clamp(20rem, 30rem, 80vw);
                    padding: 1rem;
                    background:
                        linear-gradient(
                            to top,
                            rgba(255, 251, 251, 0.46),
                            rgba(255, 251, 251, 0.64) 10%,
                            rgba(255, 251, 251, 0.86) 95%,
                            rgba(255, 251, 251, 0.46)
                        ) !important;
                    backdrop-filter: blur(5px);
                    article {
                        // revert CSS fade
                        max-height: 70vh;
                        font-size: 1rem;
                        &::after {
                            content: unset;
                        }
                    }
                    .btn {
                        background: unset;
                        &:hover {
                            box-shadow: unset;
                        }
                    }
                }
                .do-follow {
                    background: rgba(255, 255, 255, 0.85);
                }
            }
        }

        @media screen and (max-width: $breakpoint-tablet - 1) {
            .card-header .title,
            .article-ingress h2 {
                font-size: 1.35rem !important;
            }
            .ingress-text {
                font-size: 1rem !important;
            }
        }
        @media screen and (max-width: $breakpoint-mobile - 1) {
            .card-contents {
                padding-right: 0.5rem;
                padding-left: 0.5rem;
            }
            .card-header .title,
            .article-ingress h2 {
                font-size: 1.25rem !important;
            }
            .ingress-text {
                font-size: 1rem !important;
            }
        }
    }

    .hero-navigation {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 10000; // more than .hero-page, less than main .navbar
        display: flex;
        justify-items: space-between;
        width: 99vw; // adjust for browser scrollbar
        height: 100vh;
        .next-icon, .previous-icon {
            display: inline-flex;
            align-self: center;
            width: 64px;
            height: 64px;
            color: rgba(239, 239, 239, 0.92);
            cursor: pointer;
            transform: scaleY(1.5);
        }
        .next-icon {
            justify-self: right;
            margin-left: auto;
        }
        .previous-icon {
            justify-self: left;
        }
    }
}
</style>
